import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/lib/auth";
import { LayoutDashboard, Package, ClipboardList, Truck, Megaphone, LogOut } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList },
  { to: "/admin/logistics", label: "Logistics", icon: Truck },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#FAFAF8]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-border-subtle bg-white">
        <Link to="/admin" className="flex items-center gap-3 px-6 py-6 border-b border-border-subtle">
          <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-display text-xl text-ink leading-tight">Bedding Lounge</div>
            <div className="label-eyebrow !text-[10px]">Admin Suite</div>
          </div>
        </Link>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-sage/10 text-sage font-semibold" : "text-ink-soft hover:bg-cream-warm hover:text-ink"
                }`
              }
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
            >
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border-subtle">
          <div className="text-xs text-ink-soft mb-2 truncate">{admin?.email}</div>
          <button
            onClick={() => {
              logout();
              navigate("/admin/login");
            }}
            className="w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-ink-soft hover:text-rose-dark py-2 border border-border-subtle hover:border-rose"
            data-testid="admin-logout"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile bar */}
        <div className="md:hidden flex items-center justify-between p-4 bg-white border-b border-border-subtle">
          <Link to="/admin" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="" className="h-8 w-8 object-contain" />
            <span className="font-display text-lg text-ink">Admin</span>
          </Link>
          <button
            onClick={() => {
              logout();
              navigate("/admin/login");
            }}
            className="text-xs text-ink-soft uppercase tracking-widest"
            data-testid="admin-logout-mobile"
          >
            Sign out
          </button>
        </div>
        {/* Mobile nav strip */}
        <div className="md:hidden flex overflow-x-auto bg-white border-b border-border-subtle no-scrollbar">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-3 text-xs whitespace-nowrap ${
                  isActive ? "text-sage font-semibold border-b-2 border-sage" : "text-ink-soft"
                }`
              }
            >
              <n.icon size={14} /> {n.label}
            </NavLink>
          ))}
        </div>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
